import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from '../shared/services/local-storage.service';
import { WagmiConfigService } from '../shared/services/wagmi-config.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    /** Guards against a burst of parallel requests each 401ing off the same expired token
     *  running the teardown/redirect more than once. */
    private isHandlingSessionExpiry = false;

    constructor(
        private readonly localStorageService: LocalStorageService,
        private readonly router: Router,
        private readonly wagmiConfigService: WagmiConfigService
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const accessToken = this.localStorageService.getToken();
        // add auth header with access token if account is logged in and request is to the api url
        const isApiUrl = request.url.startsWith(environment.API_BASE_URL);
        const language = this.localStorageService.getDataByKey('lang') || 'en';
        // Only a request that actually carried this token can have 401'd *because the session
        // expired/was revoked* — an unauthenticated request (e.g. requesting a login nonce)
        // returning 401 means something else entirely and must not trigger a forced logout.
        const sentAuthHeader = !!(accessToken && isApiUrl);

        if (sentAuthHeader) {
            let headers = request.headers.set('lang', language);
            headers = headers.set('Authorization', 'Bearer ' + accessToken);
            request = request.clone({ headers });
        } else if (request.url.startsWith(environment.AOC_LANDING_PAGE_BASE_URL)) {
            request = request.clone({
                headers: request.headers.set('lang', language)
            });
        }

        return next.handle(request).pipe(
            catchError((error: unknown) => {
                if (sentAuthHeader && error instanceof HttpErrorResponse && error.status === 401) {
                    this.handleSessionExpired();
                }
                return throwError(() => error);
            })
        );
    }

    /**
     * Backend session token was rejected as expired/revoked while the wallet itself may still
     * read as "connected" — without this, every subsequent authenticated call on the page just
     * fails one by one with whatever generic error toast that component happens to implement,
     * instead of a clean, single re-auth prompt.
     *
     * Runs the same teardown as an explicit wallet-change/sign-out
     * (see WagmiConfigService.disconnectAndClearSession()), then routes through wallet-connect
     * so the user can reconnect and sign in again, and preserves the page they were on so they
     * land back there afterwards — the same `currentUrl` convention AuthGuard uses.
     */
    private handleSessionExpired(): void {
        if (this.isHandlingSessionExpiry) return;
        this.isHandlingSessionExpiry = true;

        if (!this.router.url.includes('/wallet-connect')) {
            localStorage.setItem('currentUrl', this.router.url);
        }

        void this.wagmiConfigService.disconnectAndClearSession().finally(() => {
            this.router.navigate(['/wallet-connect']);
            this.isHandlingSessionExpiry = false;
        });
    }
}
